import React, { useEffect, useMemo, useState } from 'react'
import { Empty, Spin } from 'antd'
import { getAicDashboardExceptions } from '../../request'
import { EMPTY, formatDateTime, unwrapRows } from './terminalModel'

const settle = promise => promise.then(value => ({ ok: true, value }), () => ({ ok: false, value: null }))

const severityColor = severity => (severity === '高' || severity === 'critical' ? '#f23645' : '#b2821a')

/** 异常中心：左列表 + 右详情，只读，不伪造接手/恢复动作。 */
export default function TerminalExceptions () {
  const [exceptions, setExceptions] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [keyword, setKeyword] = useState('')
  const [type, setType] = useState('all')
  const [status, setStatus] = useState('all')
  const [selectedId, setSelectedId] = useState(null)

  useEffect(() => {
    let mounted = true
    setLoading(true)
    setError('')
    settle(getAicDashboardExceptions({})).then(result => {
      if (!mounted) return
      if (!result.ok) {
        setError('异常列表加载失败')
        setExceptions([])
        return
      }
      const list = unwrapRows(result.value)
      setExceptions(list)
      setSelectedId(list.length > 0 ? list[0].id : null)
    }).finally(() => {
      if (mounted) setLoading(false)
    })
    return () => { mounted = false }
  }, [])

  const typeOptions = useMemo(() => Array.from(new Set(exceptions.map(item => item.type).filter(Boolean))), [exceptions])
  const statusOptions = useMemo(() => Array.from(new Set(exceptions.map(item => item.status).filter(Boolean))), [exceptions])

  const filtered = useMemo(() => exceptions.filter(item => {
    const haystack = [item.title, item.owner, item.impact, item.healthReason, item.type].filter(Boolean).join(' ').toLowerCase()
    return (!keyword || haystack.includes(keyword.toLowerCase())) &&
      (type === 'all' || item.type === type) &&
      (status === 'all' || item.status === status)
  }), [exceptions, keyword, type, status])

  const selected = filtered.find(item => String(item.id) === String(selectedId)) || filtered[0] || null
  const evidence = selected && Array.isArray(selected.evidence) ? selected.evidence : []

  return <div className="term-page">
    {error && <div className="term-notice term-notice-error">{error}</div>}
    <div className="term-page-head">
      <div className="term-page-title">异常中心</div>
      <div className="term-page-sub">异常由控制面实时聚合，只读展示；接手、恢复与通知历史暂无后端接口。</div>
    </div>
    <div className="term-toolbar">
      <input
        className="term-search"
        type="search"
        value={keyword}
        placeholder="搜索异常、影响、负责人"
        aria-label="搜索异常、影响、负责人"
        onChange={event => setKeyword(event.target.value)}
      />
      <select className="term-select" value={type} aria-label="异常类型筛选" onChange={event => setType(event.target.value)}>
        <option value="all">全部类型</option>
        {typeOptions.map(value => <option key={value} value={value}>{value}</option>)}
      </select>
      <select className="term-select" value={status} aria-label="异常状态筛选" onChange={event => setStatus(event.target.value)}>
        <option value="all">全部状态</option>
        {statusOptions.map(value => <option key={value} value={value}>{value}</option>)}
      </select>
      <span className="term-count">显示 {filtered.length} / {exceptions.length}</span>
    </div>
    <Spin spinning={loading}>
      <div className="term-exception-layout">
        <div className="term-exception-list">
          <div className="term-panel-title">异常列表（{filtered.length}）</div>
          {filtered.length === 0
            ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无异常记录" />
            : filtered.map(item => <button
              key={item.id || item.projectId}
              type="button"
              className={`term-exception-item ${selected && String(selected.id) === String(item.id) ? 'on' : ''}`}
              onClick={() => setSelectedId(item.id)}
            >
              <span className="term-exception-dot" style={{ background: severityColor(item.severity) }} />
              <span className="term-exception-body">
                <span className="term-exception-title">{item.title || item.type || EMPTY}</span>
                <span className="term-exception-meta">{item.type || EMPTY} · {item.status || EMPTY} · {formatDateTime(item.lastSeenAt)}</span>
              </span>
            </button>)}
        </div>
        <div className="term-exception-detail">
          <div className="term-panel-title">异常详情</div>
          {selected
            ? <div className="term-panel">
              <div className="term-detail-title">{selected.title || selected.type || EMPTY}</div>
              <dl className="term-kv">
                <dt>异常类型</dt><dd>{selected.type || EMPTY}</dd>
                <dt>严重程度</dt><dd>{selected.severity || EMPTY}</dd>
                <dt>影响范围</dt><dd>{selected.impact || EMPTY}</dd>
                <dt>负责人</dt><dd>{selected.owner || EMPTY}</dd>
                <dt>当前状态</dt><dd>{selected.status || EMPTY}</dd>
                <dt>最后上传时间</dt><dd>{formatDateTime(selected.lastSuccessfulUploadAt)}</dd>
                <dt>计划截止时间</dt><dd>{formatDateTime(selected.nextExpectedUploadAt)}</dd>
                <dt>首次发现</dt><dd>{formatDateTime(selected.firstSeenAt)}</dd>
                <dt>最近发现</dt><dd>{formatDateTime(selected.lastSeenAt)}</dd>
                <dt>恢复时间</dt><dd>{formatDateTime(selected.recoveredAt, '未恢复')}</dd>
                <dt>超期时长</dt><dd>{selected.overdueHours === null || selected.overdueHours === undefined ? EMPTY : `${selected.overdueHours} 小时`}</dd>
              </dl>
              <div className="term-panel-title">判定依据</div>
              {evidence.length > 0
                ? <ul className="term-evidence">{evidence.map((value, index) => <li key={`evidence-${index}`}>{value}</li>)}</ul>
                : <div className="term-muted">暂无判定依据</div>}
            </div>
            : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无异常记录" />}
        </div>
      </div>
    </Spin>
    <div className="term-foot">异常标题、类型与严重程度来自控制面；页面不提供自动修复、权限变更或数据写入。</div>
  </div>
}
